import { Buffer } from 'buffer';
import type { QueryClient } from 'react-query/core';
import { buffer2hex } from './utils/crypto';
import TokenizedApiEndpoints, {
  TMobileAuthTokenCallback,
  TMobileAuthHeadersCallback,
  TRestoreAuthTokenCallback,
  TErrorTransform,
} from './modules/TokenizedApiEndpoints';
import PersistentStorage from './modules/PersistentStorage';
import ReduxState, { TReduxState } from './modules/ReduxState';
import Account, { setTokenizedBackendAction } from './modules/Account';
import Entities from './modules/Entities';
import Treasury from './modules/Treasury';
import Contracts from './modules/Contracts';
import ContractProse from './modules/ContractProse';
import TokenizedApiQuery from './modules/TokenizedApiQuery';
import LibraryIntl from './modules/LibraryIntl';
import Activity from './modules/Activity';
import Transfers from './modules/Transfers';
import StaticResources from './modules/StaticResources';
import Banking from './modules/Banking';
import MFA from './modules/MFA';
import Relationships from './modules/Relationships';
import { TBackend } from './utils/backends';

const CLIENT_ID_PERSIST_KEY = 'clientId';

/**
 * Configuration options. All properties are optional. With no configuration,
 * the session will connect to the Tokenized production back end.
 */
export interface TSdkOptions {
  sessionId?: string | null;

  /**
   * `development`, `test`, or `production`. Each back end has separate
   * user accounts. Default is `production`.
   */
  tokenizedBackend?: TBackend;

  /**
   * Specify your API public key, assigned to you by Tokenized, providing access
   * to your individual tenant. The default is the Tokenized tenant (same user
   * accounts as the Tokenized app). You can specify an object to provide multiple
   * keys, one for each back end.
   */
  platformApiKey?:
    | string
    | null
    | {
        /** Your API public key for the development back end. */
        development?: string | null;
        /** Your API public key for the development back end. */
        test?: string | null;
        /** Your API public key for the production back end. */
        production?: string | null;
        /** Your API public key for a development back end running locally. */
        local?: string | null;
      };

  restApiBaseUrl?: string;
  sseBaseUrl?: string;
  userHandleDomain?: string;
  userAgentString?: string;

  /**
   * By default the SDK will reject log in to a new account that is
   * unverified and/or hasn’t had its recovery phrase backed up. If you
   * set `accountCreationSupported` to `true`, you are expected to
   * support both features
   */
  accountCreationSupported?: boolean;

  /** Identifies your app in authentication requests. Default is `tokenized-sdk`. */
  applicationIdentifier?: string;
  sourceSystemInfo?: string;

  /**
   * Specifies the locale to use for messages
   * and number formatting, for example `en-GB`. By default the
   * browser’s current language will be used.
   */
  locale?: string;

  /**
   * Optionally provide a function that will be called whenever the SDK detects
   * an error (typically used to report the error to a monitoring service).
   */
  errorTransform?: TErrorTransform | null;

  /**
   * Optionally provide a function that will be called
   * whenever the user is logged out. Use this to clear any sensitive user
   * information you’ve cached in your app. The function is called with no arguments.
   */
  onLogOut?: (() => void) | null;

  /**
   * Optionally provide a React Query client you want the SDK to share
   * and use for caching API response data
   */
  queryClient?: QueryClient;

  /**
   * By default the SDK listens for activity events pushed by the back end
   * (if supported), and updates activity queries immediately.
   */
  listenForActivity?: boolean;

  /**
   * By default the SDK persists the session so that the current user
   * stays logged in across a browser reload
   */
  enableSessionPersistence?: boolean;

  /**
   * By default the SDK will automatically refresh the session token
   * shortly before it expires.
   */
  enableAutomaticTokenRefresh?: boolean;

  /** @internal */
  mobileAuthToken?: string | TMobileAuthTokenCallback | null;

  /** @internal */
  mobileAuthHeaders?: TMobileAuthHeadersCallback | null;

  /**
   * If provided, the app will wait for this method to resolve in an attempt
   * to restore an externally provided authToken before initialization.
   */
  restoreAuthTokenCallback?: TRestoreAuthTokenCallback | null;
}

export interface TSdkConfig {
  sessionId: string | null;
  tokenizedBackend: TBackend;
  platformApiKey:
    | string
    | null
    | {
        development?: string | null;
        test?: string | null;
        production?: string | null;
        local?: string | null;
      };
  restApiBaseUrl?: string;
  sseBaseUrl?: string;
  userHandleDomain?: string;
  userAgentString?: string;
  accountCreationSupported: boolean;
  applicationIdentifier: string;
  sourceSystemInfo: string;
  locale?: string;
  errorTransform?: TErrorTransform | null;
  onLogOut?: (() => void) | null;
  queryClient?: QueryClient;
  listenForActivity?: boolean;
  enableSessionPersistence?: boolean;
  enableAutomaticTokenRefresh?: boolean;
  mobileAuthToken?: string | TMobileAuthTokenCallback | null;
  mobileAuthHeaders?: TMobileAuthHeadersCallback | null;
  restoreAuthTokenCallback?: TRestoreAuthTokenCallback | null;
  loginConfiguration: {
    url: string;
    redirect: string;
    client: string;
    scope: string;
  } | null;
  authResponse: string | null;
}

export const CONFIG_DEFAULTS: TSdkConfig = {
  sessionId: null,
  tokenizedBackend: 'production',
  platformApiKey: {
    development: null,
    test: null,
    production: null,
    local: null,
  },
  restApiBaseUrl: undefined,
  userHandleDomain: undefined,
  userAgentString: undefined,
  accountCreationSupported: false,
  applicationIdentifier: 'tokenized-sdk',
  sourceSystemInfo: navigator.userAgent,
  locale: undefined,
  errorTransform: null,
  onLogOut: null,
  queryClient: undefined,
  listenForActivity: true,
  enableSessionPersistence: true,
  enableAutomaticTokenRefresh: true,
  mobileAuthToken: null,
  mobileAuthHeaders: null,
  restoreAuthTokenCallback: null,
  loginConfiguration: null,
  authResponse: null,
};

/**
 * A TokenizedApi object manages user log-ins and sessions, one at a time, and
 * provides the user’s Tokenized profile data to your app’s UI code. Create one
 * TokenizedApi object globally on startup, and pass it as context throughout your app.
 *
 * #### Devtools
 *
 * The SDK stores state using [Redux](https://redux.js.org). Devtools support
 * for the internal store is turned off by default. If you’d like to view the
 * SDK’s internal state in development builds, run this code in your browser’s
 * console, and reload:
 *
 *     window.localStorage.setItem('tokenized-sdk-devtools-enable', 'true');
 *
 * In the Redux devtools browser extension, you should be able to select
 * `Tokenized SDK` from the list of stores. To turn devtools support off again, run:
 *
 *     window.localStorage.removeItem('tokenized-sdk-devtools-enable');
 */
class TokenizedApi {
  readonly config: TSdkConfig;
  readonly sessionId: string | null;

  /** @internal */
  readonly persistentState: PersistentStorage;
  /** @internal */
  readonly endpoints: TokenizedApiEndpoints;

  readonly intl: LibraryIntl;
  readonly state: ReduxState;
  readonly query: TokenizedApiQuery;
  readonly mfa: MFA;
  readonly staticResources: StaticResources;

  /** Handles user accounts and logging in */
  readonly account: Account;
  /** Handles profile switching and user settings */
  readonly entities: Entities;
  /** Handles historical and pending activity */
  readonly activity: Activity;
  /** Handles vaults, lockboxes, and instruments */
  readonly treasury: Treasury;
  /** Handles instrument transfers, requests, and trades */
  readonly transfers: Transfers;
  /** Handles contracts */
  readonly contracts: Contracts;
  /** Handles contract body of agreement editing */
  readonly contractProse: ContractProse;
  /** Handles relationships with other entities */
  readonly relationships: Relationships;
  /** Handles linked bank accounts */
  readonly banking: Banking;

  // When true, all accounts that have the `is_backedup` flag set to `false` will
  // be required to go through the backup health check process.
  /** @internal */
  static TEST_HEALTH_CHECK = false;

  /**
   * Create a new object to manage Tokenized user sessions:
   *
   * - Do this once on app startup
   * - On construction, the last session will be restored (asynchronously) from
   *   SessionStorage, if possible.
   * - On construction, the account status will be `loading`.
   * - After session restore, the account status will transition to `loggedIn` or
   *   `loggedOut`.
   *
   * #### Example
   *
   * ```js
   * import { TokenizedApi } from '@tokenized/sdk-js-private';
   * const tokenizedApi = new TokenizedApi({
   *   tokenizedBackend: 'development',
   *   applicationIdentifier: 'tokenized-example-react',
   * });
   * ```
   *
   * @param config - Configuration options. All properties are
   *   optional. With no configuration, the session will connect to the
   *   Tokenized production back end.
   */
  constructor(config?: TSdkOptions) {
    // Merge in config defaults
    this.config = {
      ...CONFIG_DEFAULTS,
      ...config,
      // For defaults with specific values, we usually want to
      // fall back to the default for any falsey value passed in
      tokenizedBackend:
        config?.tokenizedBackend || CONFIG_DEFAULTS.tokenizedBackend,
      // null is valid for the API key
      platformApiKey:
        config?.platformApiKey === null
          ? null
          : config?.platformApiKey || CONFIG_DEFAULTS.platformApiKey,
      applicationIdentifier:
        config?.applicationIdentifier || CONFIG_DEFAULTS.applicationIdentifier,
      sourceSystemInfo:
        config?.sourceSystemInfo || CONFIG_DEFAULTS.sourceSystemInfo,
    };

    // Check configuration first
    if (
      !this.config.applicationIdentifier ||
      typeof this.config.applicationIdentifier !== 'string'
    ) {
      throw new TypeError(
        'Invalid config: applicationIdentifier must be a non-empty string',
      );
    }
    if (!this.config.sessionId) {
      this.sessionId = null;
    } else {
      if (typeof this.config.sessionId !== 'string') {
        throw new TypeError(
          'Invalid config: sessionId must be a string or null',
        );
      }
      this.sessionId = this.config.sessionId;
    }
    if (
      !this.config.sourceSystemInfo ||
      typeof this.config.sourceSystemInfo !== 'string'
    ) {
      throw new TypeError(
        'Invalid config: sourceSystemInfo must be a non-empty string',
      );
    }

    // Initialize modules (order is important)
    this.persistentState = new PersistentStorage(this);
    this.intl = new LibraryIntl(this);
    this.state = new ReduxState(this);
    this.query = new TokenizedApiQuery(this);
    this.account = new Account(this);
    this.endpoints = new TokenizedApiEndpoints(this);
    this.entities = new Entities(this);
    /** Access to balances and instruments */
    this.treasury = new Treasury(this);
    /** Access to contracts */
    this.contracts = new Contracts(this);
    this.contractProse = new ContractProse(this);
    /** Access to activity */
    this.activity = new Activity(this);
    this.relationships = new Relationships(this);
    this.transfers = new Transfers(this);
    this.staticResources = new StaticResources(this);
    this.banking = new Banking(this);
    this.mfa = new MFA(this);

    // Write back end into Redux (for QR codes)
    this.state.dispatch(
      setTokenizedBackendAction(this.config.tokenizedBackend),
    );

    // Always try to restore the previous session on startup
    this.state.dispatch(this.account.restoreSessionAction());

    this.account.checkAccessTokens();
  }

  /** @internal */
  mount() {
    // Turns on React Query’s monitoring of
    // window focus and network connection state
    this.getQueryClient().mount();
  }

  /** @internal */
  unmount() {
    this.getQueryClient().unmount();
  }

  /** @internal */
  async getApplicationIdentifiers() {
    let clientId = this.persistentState.getGlobalItem(CLIENT_ID_PERSIST_KEY);
    if (!clientId) {
      const clientIdBuffer = await window.crypto.subtle.digest(
        'SHA-256',
        Buffer.from(
          `${this.config.applicationIdentifier}-${new Date().toISOString()}`,
        ),
      );
      clientId = buffer2hex(clientIdBuffer);
      this.persistentState.setGlobalItem(CLIENT_ID_PERSIST_KEY, clientId);
    }
    return {
      clientId,
      sourceName: this.config.sourceSystemInfo,
    };
  }

  /**
   * Provides the [React Query `QueryClient`
   * object](https://react-query.tanstack.com/reference/QueryClient) that
   * manages the API data cache for the current user session. Use to observe
   * query data.
   */
  getQueryClient() {
    return this.query.getQueryClient();
  }

  /**
   * Reloads all model data from the back end, automatically updating
   * any active queries bound to UI. This should only be used in exceptional
   * circumstances, for example when you know an external event has modified
   * the current account and you want to show the change immediately. All
   * operations performed through the SDK will efficiently update just
   * the necessary queries for you: don’t call `reloadAll` since it will
   * unnecessarily throw away valid data.
   */
  async reloadAll() {
    await this.query.reloadAll();
  }

  getReduxStore() {
    return this.state.store;
  }

  getReduxState(): TReduxState {
    return this.state.getState();
  }

  subscribeToSelector<S>(
    selector: (state: TReduxState) => S,
    onChange: (currentState: S) => void,
    notifyInitialValue: boolean,
  ) {
    return this.state.subscribeToSelector(
      selector,
      onChange,
      notifyInitialValue,
    );
  }

  static async clearCaches() {
    const cache = await caches.open('network');
    for (const key of await cache.keys()) {
      await cache.delete(key);
    }
  }
}

export default TokenizedApi;

export {
  TokenizedApplicationError,
  TokenizedSystemError,
  TokenizedError,
  TokenizedApiError,
  TokenizedNetworkError,
  TokenizedValidationError,
  TokenizedAuthError,
  TokenizedResponseInvalidError,
  TokenizedInsufficientFundsError,
  TokenizedServiceError,
  TokenizedExternalServiceError,
  TokenizedNonTransferableInstrumentError,
  TokenizedAttestationError,
  TokenizedPermissionRestrictionError,
} from './utils/errors';
export type {
  TErrorInfo,
  TSerializedError,
  TAttestationFailureReason,
} from './utils/errors';

export type { TBackend } from './utils/backends';

// These have to be exported for TypeScript to
// successfully generate type declarations
export type {
  SchemaValue,
  TSchemaValueType,
  IdSchemaValue,
  ReferenceSchemaValue,
  ContainerSchemaValue,
  SelectTypeSchemaValue,
} from './utils/schema';

export type {
  LibraryIntl,
  ReduxState,
  TokenizedApiQuery,
  MFA,
  StaticResources,
  Account,
  Entities,
  Activity,
  Treasury,
  Transfers,
  Contracts,
  ContractProse,
  Relationships,
  Banking,
};

export { makeReduxStore } from './modules/ReduxState';
export type {
  TReduxState,
  TReduxStore,
  TThunkAction,
  ReturnTypeFromActionType,
} from './modules/ReduxState';

export {
  getNumberParseInfo,
  EVENT_INTLCHANGE_TYPE,
  isIntlChangeEvent,
} from './modules/LibraryIntl';
export type {
  TSdkIntlShape,
  TIntlChangeEventDetail,
  TIntlChangeEvent,
} from './modules/LibraryIntl';

export {
  HANDSHAKE_STATUSES,
  ENTITY_TYPES,
  ACTIVITY_TYPES,
  REORG_ACTIVITY_STAGES,
  ACTIVITY_STAGES,
  TERMINATION_REASONS,
} from './modules/TokenizedApiModelEntities';
export type {
  THandshakeStatus,
  TDevice,
  THandshake,
  TRenewedToken,
  THandleAvailability,
  TEmailAvailability,
  TAccountRecoveryDetails,
  TUser,
  TProfile,
  TSigner,
  TEntityType,
  TEntity,
  TLockbox,
  TVault,
  TInstrumentBalance,
  TLockboxBalanceList,
  TNetEquityHistory,
  TContact,
  TCurrencyDetails,
  TCurrencyExchangeRate,
  TFaceValue,
  TAgeRestriction,
  TInstrumentCCYSpecifics,
  TInstrumentCRNSpecifics,
  TInstrumentCOUSpecifics,
  TInstrumentLOYSpecifics,
  TInstrumentRPTSpecifics,
  TInstrumentCHPSpecifics,
  TInstrumentTICSpecifics,
  TInstrumentDetailsCurrency,
  TInstrumentDetailsCreditNote,
  TInstrumentDetailsCoupon,
  TInstrumentDetailsLoyalty,
  TInstrumentDetailsReward,
  TInstrumentDetailsCasino,
  TInstrumentDetailsTicket,
  TInstrumentDetails,
  TActivityType,
  TReorgActivityStage,
  TActivityStage,
  TTerminationReason,
  TActivityTransactionSigner,
  TActivityTransaction,
  TActivityTransfer,
  TActivityCounterparty,
  TActivityContractDetails,
  TActivityInstrumentDetails,
  TActivityFees,
  TActivityOverview,
  TActivityDetails,
  TNewActivityCreated,
  THandshakeRequiredNoActivity,
  TPermissionRestrictionMessage,
  TFeePaidInInstruments,
} from './modules/TokenizedApiModelEntities';

export type {
  TErrorTransform,
  TMobileAuthTokenCallback,
  TMobileAuthHeadersCallback,
  TRestoreAuthTokenCallback,
} from './modules/TokenizedApiEndpoints';

export {
  TokenizedMfaError,
  TokenizedMfaNotFoundError,
  TokenizedMfaCancelledError,
  TokenizedMfaDeniedError,
  handshakeConfirmationCode,
  MFARequest,
} from './modules/MFA';

export {
  Instrument,
  DEFAULT_CONTRACT_ACTION_FEE,
  BSV_MINIMUM_TRANSFER_QUANTITY,
  CURRENCY_BSV,
  CURRENCY_USD,
  CURRENCY_CAD,
  CURRENCY_AUD,
  CURRENCY_NZD,
  CURRENCY_EUR,
  CURRENCY_GBP,
  CURRENCY_SGD,
  CURRENCY_DEFAULT,
  INSTRUMENT_TYPE_CURRENCY,
  INSTRUMENT_TYPE_UNKNOWN,
  INSTRUMENT_TYPE_CREDIT_NOTE,
  INSTRUMENT_TYPE_COUPON,
  INSTRUMENT_TYPE_LOYALTY,
  INSTRUMENT_TYPE_REWARD,
  INSTRUMENT_TYPE_CASINO,
  INSTRUMENT_TYPE_TICKET,
  INSTRUMENT_TYPE_FIXED_BOND,
  INSTRUMENT_TYPE_VOUCHER,
  BLOCKCHAIN_CURRENCIES,
  DISPLAY_CURRENCIES,
  MAX_DISPLAY_PRECISION,
  MIN_EXCHANGE_RATE_PRECISION,
  CURRENCY_DEFAULT_PRECISION,
  defaultUnitPrecisionForCurrency,
  INSTRUMENT_TYPE_ORDER,
  MONEY_INSTRUMENT_TYPES,
  isMoneyInstrumentType,
  isInstrumentDetailsCurrency,
  isInstrumentDetailsCreditNote,
  isInstrumentDetailsCoupon,
  isInstrumentDetailsLoyalty,
  isInstrumentDetailsReward,
  isInstrumentDetailsCasino,
  isInstrumentDetailsTicket,
} from './utils/instruments';
export type {
  TFormatAmountExtendedPart,
  TFormatAmountPart,
  TFormatAmountPartsResult,
  TMoneyInstrumentType,
  TInstrumentOptions,
  TInstrumentDescription,
  TInstrumentQuantityOptions,
  TFormatAmountPartsOptions,
  TFormatTokenQuantityPart,
  TContract,
} from './utils/instruments';

export {
  selectQueryEnabledStatus,
  selectIsLoading,
  selectIsLoggingIn,
  selectLogInProgress,
  selectIsLoggedOut,
  selectLogInIdentifier,
  selectLogInIdentifierType,
  selectVerificationEmailAddress,
  selectLogInIsValid,
  selectLogInNeedsMfa,
  selectLogInNeedsPersonalProfile,
  selectLogInNeedsAuthenticatorUpdate,
  selectLogInNeedsBackupHealthCheck,
  selectLogInConfirmationCode,
  selectPermissionRestrictionsAtLogIn,
  selectIsWaitingForDevicePairing,
  selectLogInPairingCodePayload,
  selectIsLoggedIn,
  selectLogInError,
  selectPreAuthToken,
  selectTokenizedBackend,
  VERIFY_SCOPES,
  TokenizedLogInCredentialsError,
  TokenizedLogInDeniedError,
  TokenizedLogInCancelledError,
  TokenizedLogInInvalidAccount,
  TokenizedAccountVerificationInvalidError,
  TokenizedSignUpEmailRestrictedError,
} from './modules/Account';
export type {
  TVerificationCodeScope,
  TAccountIdentifier,
  TAccountIdentifierType,
  TAccountState,
  TDisplayUser,
  TLogInChecking,
  TLogInProgress,
  TLogInChecklist,
  TDecodedToken,
  TParsedAccountIdentifier,
} from './modules/Account';

export type {
  TFilteredBalance,
  TBalancesQueryOptions,
  TTransformedLockbox,
  TDisplayLockbox,
} from './modules/Treasury';

export type { default as ExchangeRates } from './utils/ExchangeRates';

export type { TInstrumentSendRecipient } from './modules/Transfers';

export type {
  TCurrencyResource,
  TPolityResource,
  TBankingPolityResource,
  TStaticResources,
} from './modules/StaticResources';

export type { TDisplayContact } from './modules/Relationships';

export { calculateContractAmendmentDiff } from './utils/contracts/contractAmendmentDiffs';
export { validateContractDraft } from './utils/contracts/contractDraftValidation';
export * from './utils/contracts/contractEdits';
export {
  draftRawToMarkdown,
  stripLeadingAndTrailingWhitespace,
} from './utils/contracts/markdownConversion';
